import type { TableBillingSegmentResponse, TableConsumeInfoExt, TablePauseInfo } from "@/api/business/types/table"
import type { OrderDetailVO, TableRecord } from "@/api/user/types/member"
import type { TableChargePrintDetail } from "@/utils/receiptGenerator"
import dayjs from "dayjs"
import { buildTableBillingDetailRows } from "@/views/page/business/components/Order/tableBillingDetailRows"
import { computedMinutes } from "./datetime"
import { convertCentsToYuan } from "./math"

export interface BuildTableChargePrintDetailsOptions {
  fallbackBillingRule?: unknown
  isPreOpen?: boolean
  pauseRecordList?: TablePauseInfo[]
  tableRuleList?: unknown[]
}

export interface BuildOrderTableChargePrintDetailsOptions extends BuildTableChargePrintDetailsOptions {
  billingSegments?: TableBillingSegmentResponse[]
  tableFeeList?: TableConsumeInfoExt[]
}

export interface TableChargeReceiptRow {
  amount: string
  label: string
}

export interface TableChargeReceiptGroup {
  rows: TableChargeReceiptRow[]
  title?: string
}

export interface TableChargeReceiptView {
  endTime?: string
  groups: TableChargeReceiptGroup[]
  startTime?: string
}

export function isAuthoritativeTableBillingSegment(segment: TableBillingSegmentResponse) {
  const feeValues = [segment.tableFee, segment.overtimeFee, segment.serviceFee, segment.totalFee]
  return segment.feeCalculated !== false
    && feeValues.some(amount => amount !== undefined && amount !== null)
    && feeValues.every(isValidOptionalFee)
    && hasConsistentFeeBreakdown(segment)
}

function isValidOptionalFee(amount: number | null | undefined) {
  if (amount == null) {
    return true
  }
  const value = Number(amount)
  return Number.isFinite(value) && value >= 0
}

function hasConsistentFeeBreakdown(segment: TableBillingSegmentResponse) {
  const tableFee = segment.tableFee == null ? undefined : Number(segment.tableFee)
  const serviceFee = segment.serviceFee == null ? 0 : Number(segment.serviceFee)
  const totalFee = segment.totalFee == null ? undefined : Number(segment.totalFee)
  if (tableFee !== undefined && totalFee !== undefined && !isSameCentAmount(totalFee, tableFee + serviceFee)) {
    return false
  }
  if (tableFee === undefined && totalFee !== undefined && serviceFee > totalFee) {
    return false
  }
  if (segment.overtimeFee == null) {
    return true
  }
  const overtimeFee = Number(segment.overtimeFee)
  const combinedTableFee = tableFee ?? (totalFee === undefined ? undefined : totalFee - serviceFee)
  return combinedTableFee !== undefined
    && overtimeFee <= combinedTableFee
}

function isSameCentAmount(left: number, right: number) {
  return Math.round(left) === Math.round(right)
}

export function hasAuthoritativeTableBillingSegments(
  segments: TableBillingSegmentResponse[] | null | undefined,
): segments is TableBillingSegmentResponse[] {
  return Boolean(segments?.length && segments.every(isAuthoritativeTableBillingSegment))
}

export function buildOrderTableChargePrintDetails(
  data: OrderDetailVO,
  options: BuildOrderTableChargePrintDetailsOptions = {},
): TableChargePrintDetail[] {
  if (hasAuthoritativeTableBillingSegments(options.billingSegments)) {
    return buildTableChargePrintDetailsFromSegments(options.billingSegments)
  }
  if (options.tableFeeList?.length) {
    return buildTableChargePrintDetailsFromFeeList(options.tableFeeList, options)
  }
  return buildFallbackTableChargePrintDetails(data)
}

export function buildTableChargePrintDetailsFromSegments(
  segments: TableBillingSegmentResponse[],
): TableChargePrintDetail[] {
  return segments.flatMap((segment) => {
    if (!isAuthoritativeTableBillingSegment(segment)) {
      return []
    }
    const endTime = formatPrintTime(parseTime(segment.chargeUntilTime ?? segment.endTime))
    const commonDetail = {
      billingEndTime: endTime,
      durationText: normalizeOptionalText(segment.stayDuration)
        || (segment.stayMinutes == null ? undefined : computedMinutes(Math.max(segment.stayMinutes, 0))),
      endTime,
      ruleText: normalizeOptionalText(
        segment.ruleName || segment.billingRuleSummaryText || segment.ruleTypeName,
      ),
      sourceText: normalizeOptionalText(segment.segmentSummaryText || segment.segmentSourceName),
      startTime: formatPrintTime(parseTime(segment.startTime)),
      tableName: normalizeOptionalText(segment.tableName),
    }
    const serviceFee = toNonNegativeCents(segment.serviceFee)
    const totalFee = toNonNegativeCents(segment.totalFee)
    const combinedTableFee = segment.tableFee == null
      ? Math.max(totalFee - serviceFee, 0)
      : toNonNegativeCents(segment.tableFee)
    const hasOvertimeBreakdown = segment.overtimeFee !== undefined && segment.overtimeFee !== null
    const overtimeFee = hasOvertimeBreakdown ? toNonNegativeCents(segment.overtimeFee) : 0
    const tableFee = hasOvertimeBreakdown
      ? Math.max(combinedTableFee - overtimeFee, 0)
      : combinedTableFee
    const details = [
      buildServerSegmentChargeDetail("桌台费", tableFee, commonDetail),
      buildServerSegmentChargeDetail("超时费", overtimeFee, commonDetail),
      buildServerSegmentChargeDetail("服务费", serviceFee, commonDetail),
    ].filter((detail): detail is TableChargePrintDetail => Boolean(detail))
    return details.length > 0
      ? details
      : buildTimelineMarker(commonDetail)
  })
}

function buildServerSegmentChargeDetail(
  label: string,
  amount: number | null | undefined,
  commonDetail: Omit<TableChargePrintDetail, "amount" | "label" | "originalAmount">,
): TableChargePrintDetail | undefined {
  const normalizedAmount = toNonNegativeCents(amount)
  if (normalizedAmount <= 0) {
    return undefined
  }
  const value = convertCentsToYuan(normalizedAmount, false)
  return {
    ...commonDetail,
    amount: value,
    label,
    originalAmount: value,
  }
}

export function buildTableChargePrintDetailsFromFeeList(
  tableFeeList: TableConsumeInfoExt[],
  options: BuildTableChargePrintDetailsOptions = {},
): TableChargePrintDetail[] {
  return tableFeeList.flatMap((item) => {
    const rows = buildTableBillingDetailRows({
      fallbackBillingRule: options.fallbackBillingRule,
      isPreOpen: options.isPreOpen ?? false,
      tableFeeList: [item],
      tableRuleList: options.tableRuleList ?? [],
    })

    const segmentRange = resolveTableConsumePrintTimeRange(item)
    const details = rows
      .filter(row => row.amount > 0)
      .map((row) => {
        const range = resolveSegmentPrintTimeRange(item, row.label, row.durationText, options.pauseRecordList)
        return {
          label: normalizeTableChargeLabel(row.label),
          tableName: normalizeOptionalText(item.tableName),
          sourceText: row.sourceText,
          ruleText: row.ruleText,
          startTime: segmentRange.startTime,
          endTime: segmentRange.endTime,
          durationText: row.durationText,
          pauseDurationText: range.pauseDurationText,
          pauseSummaryText: range.pauseSummaryText,
          billingEndTime: segmentRange.endTime,
          amount: convertCentsToYuan(row.amount, false),
          originalAmount: convertCentsToYuan(row.originalAmount, false),
        }
      })
    if (details.length > 0) {
      return details
    }
    return buildTimelineMarker({
      billingEndTime: segmentRange.endTime,
      endTime: segmentRange.endTime,
      ruleText: normalizeOptionalText(item.ruleName),
      startTime: segmentRange.startTime,
      tableName: normalizeOptionalText(item.tableName),
    })
  })
}

export function buildFallbackTableChargePrintDetails(data: OrderDetailVO): TableChargePrintDetail[] {
  const tableRecordInfo = data.tableRecordInfo?.[0]
  if (!tableRecordInfo) {
    return []
  }

  const details: TableChargePrintDetail[] = []
  const range = resolveTableRecordPrintTimeRange(tableRecordInfo, data)
  const ruleText = normalizeOptionalText(tableRecordInfo.tableBillingRuleName)
  const tableFeeAmount = getFallbackTableFeeAmount(tableRecordInfo)
  if (tableFeeAmount > 0) {
    details.push({
      label: "桌台费",
      tableName: normalizeOptionalText(tableRecordInfo.tableName),
      ruleText,
      startTime: range.startTime,
      endTime: range.endTime,
      durationText: range.durationText,
      amount: convertCentsToYuan(tableFeeAmount, false),
      originalAmount: convertCentsToYuan(tableFeeAmount, false),
    })
  }

  const serviceAmount = Math.max(tableRecordInfo.serviceTotalFee ?? tableRecordInfo.serviceFee ?? 0, 0)
  if (serviceAmount > 0) {
    details.push({
      label: "服务费",
      tableName: normalizeOptionalText(tableRecordInfo.tableName),
      ruleText,
      startTime: range.startTime,
      endTime: range.endTime,
      durationText: range.durationText,
      amount: convertCentsToYuan(serviceAmount, false),
      originalAmount: convertCentsToYuan(serviceAmount, false),
    })
  }

  return details
}

export function buildTableChargeReceiptView(
  details: TableChargePrintDetail[] | null | undefined,
): TableChargeReceiptView {
  const visibleDetails = (details ?? []).flatMap((detail) => {
    const amount = resolvePositiveAmount(detail)
    if (!amount) {
      return []
    }
    return [{
      amount,
      detail,
      identity: [
        normalizeOptionalText(detail.tableName) ?? "",
        normalizeOptionalText(detail.ruleText) ?? "",
        normalizeOptionalText(detail.startTime) ?? "",
        normalizeOptionalText(detail.billingEndTime ?? detail.endTime) ?? "",
      ].join("\u0000"),
      label: normalizeTableChargeLabel(detail.label),
    }]
  })

  const groups: TableChargeReceiptGroup[] = []
  let previousIdentity: string | undefined
  let previousRank = -1
  visibleDetails.forEach((entry) => {
    const rank = tableChargeLabelRank(entry.label)
    if (!groups.length || entry.identity !== previousIdentity || rank <= previousRank) {
      const tableName = normalizeOptionalText(entry.detail.tableName)
      const ruleText = normalizeOptionalText(entry.detail.ruleText)
      groups.push({
        rows: [],
        ...(tableName || ruleText
          ? { title: [tableName, ruleText].filter(Boolean).join(" / ") }
          : {}),
      })
    }
    groups[groups.length - 1].rows.push({
      amount: entry.amount,
      label: entry.label,
    })
    previousIdentity = entry.identity
    previousRank = rank
  })

  const startTime = resolveOverallTime(details ?? [], true)
  const endTime = resolveOverallTime(details ?? [], false)
  return {
    groups,
    ...(startTime ? { startTime } : {}),
    ...(endTime ? { endTime } : {}),
  }
}

function buildTimelineMarker(
  commonDetail: Omit<TableChargePrintDetail, "amount" | "label" | "originalAmount">,
): TableChargePrintDetail[] {
  if (!commonDetail.startTime && !commonDetail.endTime && !commonDetail.billingEndTime) {
    return []
  }
  return [{
    ...commonDetail,
    amount: "0.00",
    label: "桌台费",
    originalAmount: "0.00",
  }]
}

export function buildTableChargeFeeSummary(
  details: TableChargePrintDetail[] | null | undefined,
): Array<{ text: string, value: string }> {
  const totals = new Map<string, number>()
  for (const detail of details ?? []) {
    const amount = resolvePositiveAmount(detail)
    if (!amount) {
      continue
    }
    const cents = Math.round(Number(amount) * 100)
    if (!Number.isFinite(cents) || cents <= 0) {
      continue
    }
    const label = normalizeTableChargeLabel(detail.label)
    totals.set(label, (totals.get(label) ?? 0) + cents)
  }
  return ["桌台费", "超时费", "服务费"].flatMap((label) => {
    const amount = totals.get(label) ?? 0
    return amount > 0
      ? [{ text: label, value: convertCentsToYuan(amount, false) }]
      : []
  })
}

function resolvePositiveAmount(detail: TableChargePrintDetail) {
  const amount = normalizeOptionalText(detail.amount) ?? normalizeOptionalText(detail.originalAmount)
  if (!amount) {
    return undefined
  }
  const numericAmount = Number(amount)
  if (Number.isFinite(numericAmount)) {
    return numericAmount > 0 ? amount : undefined
  }
  return amount
}

function resolveOverallTime(details: TableChargePrintDetail[], startTime: boolean) {
  return details.reduce<string | undefined>((resolved, detail) => {
    const value = normalizeOptionalText(startTime
      ? detail.startTime
      : detail.billingEndTime ?? detail.endTime)
    if (!value) {
      return resolved
    }
    if (!resolved) {
      return value
    }
    const parsedValue = parseTime(value)
    const parsedResolved = parseTime(resolved)
    if (parsedValue && parsedResolved) {
      const shouldReplace = startTime
        ? parsedValue.isBefore(parsedResolved)
        : parsedValue.isAfter(parsedResolved)
      return shouldReplace ? value : resolved
    }
    return startTime
      ? (value < resolved ? value : resolved)
      : (value > resolved ? value : resolved)
  }, undefined)
}

function normalizeTableChargeLabel(label: unknown) {
  const normalized = normalizeOptionalText(label)
  if (normalized?.includes("服务")) {
    return "服务费"
  }
  if (normalized?.includes("超时")) {
    return "超时费"
  }
  return "桌台费"
}

function tableChargeLabelRank(label: string) {
  if (label === "超时费") {
    return 1
  }
  if (label === "服务费") {
    return 2
  }
  return 0
}

function toNonNegativeCents(amount: number | null | undefined) {
  return Math.max(Math.round(Number(amount ?? 0)) || 0, 0)
}

function resolveTableConsumePrintTimeRange(item: TableConsumeInfoExt) {
  const checkIn = parseTime(item.checkIn)
  const checkOut = parseTime(item.checkOut) ?? (checkIn ? dayjs() : undefined)
  return {
    startTime: formatPrintTime(checkIn),
    endTime: formatPrintTime(checkOut),
  }
}

function resolveSegmentPrintTimeRange(
  item: TableConsumeInfoExt,
  label: string,
  durationText: string,
  pauseRecordList: TablePauseInfo[] = [],
) {
  const checkIn = parseTime(item.checkIn)
  const checkOut = parseTime(item.checkOut) ?? (checkIn ? dayjs() : undefined)
  const packageDuration = Number(item.packageDuration ?? 0)
  const durationMinutes = parseDurationTextToMinutes(durationText)
  const pauseIntervals = normalizePauseIntervals(pauseRecordList)

  if (label === "包段收费" && checkIn) {
    const packageEnd = addBillableMinutes(checkIn, durationMinutes ?? packageDuration, pauseIntervals)
    const endTime = checkOut && checkOut.isBefore(packageEnd) ? checkOut : packageEnd
    const pauseMinutes = calculatePauseOverlapMinutes(checkIn, endTime, pauseIntervals)
    const formattedEndTime = formatPrintTime(endTime)
    return {
      startTime: formatPrintTime(checkIn),
      endTime: formattedEndTime,
      billingEndTime: formattedEndTime,
      pauseDurationText: formatPauseDuration(pauseMinutes),
      pauseSummaryText: pauseMinutes > 0 && formattedEndTime
        ? `暂停 ${computedMinutes(pauseMinutes)}，包段顺延至 ${formattedEndTime}`
        : undefined,
    }
  }

  if (label === "超时收费" && checkIn && packageDuration > 0) {
    const overtimeStart = addBillableMinutes(checkIn, packageDuration, pauseIntervals)
    const pauseMinutes = checkOut ? calculatePauseOverlapMinutes(overtimeStart, checkOut, pauseIntervals) : 0
    return {
      startTime: formatPrintTime(overtimeStart),
      endTime: formatPrintTime(checkOut),
      pauseDurationText: formatPauseDuration(pauseMinutes),
      pauseSummaryText: pauseMinutes > 0
        ? `暂停 ${computedMinutes(pauseMinutes)}，超时已扣除暂停`
        : undefined,
    }
  }

  const pauseMinutes = checkIn && checkOut ? calculatePauseOverlapMinutes(checkIn, checkOut, pauseIntervals) : 0
  return {
    startTime: formatPrintTime(checkIn),
    endTime: formatPrintTime(checkOut),
    pauseDurationText: formatPauseDuration(pauseMinutes),
    pauseSummaryText: pauseMinutes > 0 ? `暂停 ${computedMinutes(pauseMinutes)}，已扣除暂停` : undefined,
  }
}

function resolveTableRecordPrintTimeRange(tableRecordInfo: TableRecord, data: OrderDetailVO) {
  const checkIn = parseTime(tableRecordInfo.checkIn)
  const checkOut = parseTime(tableRecordInfo.billingCheckoutTime)
    ?? parseTime(tableRecordInfo.checkOut)
    ?? parseTime(data.orderInfo?.orderFinishTime)
    ?? parseTime(data.orderInfo?.createTime)
    ?? (checkIn ? dayjs() : undefined)

  return {
    startTime: formatPrintTime(checkIn),
    endTime: formatPrintTime(checkOut),
    durationText: tableRecordInfo.billingStayDuration
      || computedMinutes(tableRecordInfo.billingStayMinutes ?? tableRecordInfo.stayMinutes ?? 0),
  }
}

function getFallbackTableFeeAmount(tableRecordInfo: TableRecord) {
  const mergeRecord = tableRecordInfo.mergeRecord ?? []
  const mergeTableFee = mergeRecord.reduce((total, item) => total + (item.tableFee ?? 0), 0)
  return Math.max((tableRecordInfo.tableFee ?? 0) + mergeTableFee, 0)
}

function parseTime(value: unknown) {
  if (!value) {
    return undefined
  }
  const parsed = dayjs(value as string | number | Date)
  return parsed.isValid() ? parsed : undefined
}

function formatPrintTime(value: dayjs.Dayjs | undefined) {
  return value?.isValid() ? value.format("YYYY-MM-DD HH:mm") : undefined
}

type PauseInterval = {
  start: dayjs.Dayjs
  end: dayjs.Dayjs
}

function normalizePauseIntervals(pauseRecordList: TablePauseInfo[]): PauseInterval[] {
  const sortedIntervals = pauseRecordList
    .filter(record => record.pauseStatus !== 3)
    .map((record) => {
      const start = parseTime(record.pauseStartTime)
      const end = parseTime(record.pauseEndTime)
      return start && end && end.isAfter(start) ? { start, end } : undefined
    })
    .filter((item): item is PauseInterval => Boolean(item))
    .sort((first, second) => first.start.valueOf() - second.start.valueOf())

  return sortedIntervals.reduce<PauseInterval[]>((merged, interval) => {
    const last = merged[merged.length - 1]
    if (!last || interval.start.isAfter(last.end)) {
      merged.push(interval)
      return merged
    }
    if (interval.end.isAfter(last.end)) {
      last.end = interval.end
    }
    return merged
  }, [])
}

function addBillableMinutes(start: dayjs.Dayjs, billableMinutes: number, pauseIntervals: PauseInterval[]) {
  let cursor = start
  let remainingMinutes = Math.max(Math.floor(Number(billableMinutes) || 0), 0)
  if (remainingMinutes === 0) {
    return cursor
  }

  pauseIntervals.forEach((pause) => {
    if (remainingMinutes <= 0 || pause.end.isSame(cursor) || pause.end.isBefore(cursor)) {
      return
    }
    if (pause.start.isSame(cursor) || pause.start.isBefore(cursor)) {
      if (pause.end.isAfter(cursor)) {
        cursor = pause.end
      }
      return
    }

    const billableBeforePause = pause.start.diff(cursor, "minute")
    if (remainingMinutes <= billableBeforePause) {
      cursor = cursor.add(remainingMinutes, "minute")
      remainingMinutes = 0
      return
    }
    remainingMinutes -= billableBeforePause
    cursor = pause.end.isAfter(pause.start) ? pause.end : pause.start
  })

  return remainingMinutes > 0 ? cursor.add(remainingMinutes, "minute") : cursor
}

function calculatePauseOverlapMinutes(start: dayjs.Dayjs, end: dayjs.Dayjs, pauseIntervals: PauseInterval[]) {
  if (!end.isAfter(start)) {
    return 0
  }
  return pauseIntervals.reduce((total, pause) => {
    const overlapStart = pause.start.isAfter(start) ? pause.start : start
    const overlapEnd = pause.end.isBefore(end) ? pause.end : end
    return overlapEnd.isAfter(overlapStart) ? total + overlapEnd.diff(overlapStart, "minute") : total
  }, 0)
}

function formatPauseDuration(minutes: number) {
  return minutes > 0 ? computedMinutes(minutes) : undefined
}

function parseDurationTextToMinutes(durationText?: string) {
  if (!durationText) {
    return undefined
  }
  const hourMatch = durationText.match(/(\d+(?:\.\d+)?)\s*小时/)
  const minuteMatch = durationText.match(/(\d+(?:\.\d+)?)\s*分钟/)
  if (!hourMatch && !minuteMatch) {
    return undefined
  }
  return Math.floor(Number(hourMatch?.[1] ?? 0) * 60 + Number(minuteMatch?.[1] ?? 0))
}

function normalizeOptionalText(value: unknown) {
  return typeof value === "string" && value.trim() ? value.trim() : undefined
}
